import { Observable } from "rxjs";
import {
	CashPlusPortfolioState,
	CashPlusPortfolioStateService
} from "@modules/cashplus/+state/cashplus-portfolio.state.service";
import { filter, map, withLatestFrom } from "rxjs/operators";
import { CashPlusRemote } from "@modules/cashplus/infrastructure/cashplus.remote";
import { Injectable } from "@angular/core";
import { CashPlusPortfolioApi } from "@modules/cashplus/entities/cashplus.model";
import { updateArray } from "@helpers/utils";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";

@Injectable({ providedIn: "root" })
export class CashplusPortfolioFacade {
	// The errorApi has to be declared here because it's need it one cashplus.facade.ts
	public errorApi: boolean = false;

	public constructor(
		private readonly cashPlusPortfolioState: CashPlusPortfolioStateService,
		private readonly launchDarkly: LaunchDarklyService,
		private readonly cashPlusRemote: CashPlusRemote
	) {}

	public isCashPlusEnabled$(): Observable<boolean> {
		return this.launchDarkly.isActive$(LD_FLAG.CASH_PLUS);
	}

	public getPortfolioState$(): Observable<CashPlusPortfolioState> {
		return this.cashPlusPortfolioState.getState$().pipe(filter((state) => !!state));
	}

	public getPortfolioUnfilteredState$(): Observable<CashPlusPortfolioState> {
		return this.cashPlusPortfolioState.getState$();
	}

	public setFundReleasePending(): void {
		this.cashPlusPortfolioState.updateState({ isFundReleasePending: true });
	}

	public setFundReleaseFinished(): void {
		this.cashPlusPortfolioState.updateState({ isFundReleasePending: false });
	}

	public getPortfolioPerFiatState$(fiatPid: string): Observable<CashPlusPortfolioApi> {
		return this.cashPlusPortfolioState.getState$().pipe(
			filter((state) => !!state),
			map((state: CashPlusPortfolioState) => state?.fiatConfigs?.find((api) => api.asset_pid === fiatPid))
		);
	}

	public getPortfolioPerFiatStateUnFiltered$(fiatPid: string): Observable<CashPlusPortfolioApi> {
		return this.cashPlusPortfolioState
			.getState$()
			.pipe(
				map(
					(state: CashPlusPortfolioState) =>
						state && state?.fiatConfigs?.find((api) => api.asset_pid === fiatPid)
				)
			);
	}

	public fetchPortfolioConfigForFiatPid(fiats: string[]): void {
		if (fiats?.length === 0) {
			this.cashPlusPortfolioState.updateState({
				error: false
			});
			return;
		}
		// The errorApi is forcing the error === true if any of the PIDs fail, it's need it for cashPlus Widget Error State
		this.errorApi = false;

		// for each fiat we make a separate request and update the state separately (instead of combining them all together)
		fiats
			?.map((fiat) => this.cashPlusRemote.getPortfolioAssetFiats$(fiat))
			.forEach((obs) => {
				obs.pipe(withLatestFrom(this.cashPlusPortfolioState.getState$())).subscribe({
					next: ([fiatConfig, state]) => {
						let updatedState: CashPlusPortfolioApi[];
						if (state && state.fiatConfigs) {
							// Rebuild the old array with updated values and new items
							updatedState = updateArray(state.fiatConfigs, [fiatConfig], "asset_pid");
						}
						this.cashPlusPortfolioState.updateState({
							fiatConfigs: updatedState ?? [fiatConfig],
							error: this.errorApi
						});
					},
					error: (err) => {
						// In case of 404, we don't want to set state as errored, because 404 is an OK (empty) response
						if (err.name === "NotFound") {
							return;
						}
						this.errorApi = true;
						this.cashPlusPortfolioState.updateState({
							error: this.errorApi,
							fiatConfigs: []
						});
					}
				});
			});
	}
}
